import React, { useCallback, useMemo, useState } from 'react';
import { Badge, Card, Table } from 'react-bootstrap';
import {
    badgeVariant,
    flattenDiff,
    formatTime,
    getStatusChange,
    prettyField,
} from './historyUi';

function getEmailInfo(historyItem) {
    const email = historyItem?.changes?.meta?.email;
    const requested = email?.requested || {};
    const outboxId = email?.outboxId ?? null;

    const sendToCustomer = !!requested.sendToCustomer;
    const sendToSupplier = !!requested.sendToSupplier;

    const getState = (flag) => {
        if (!flag) return { label: 'OFF', bg: 'secondary', hint: 'Not requested' };
        if (outboxId) return { label: 'ON', bg: 'success', hint: 'Queued (outbox created)' };
        return { label: 'ON', bg: 'warning', hint: 'Requested but not queued' };
    };

    return {
        outboxId,
        customer: getState(sendToCustomer),
        supplier: getState(sendToSupplier),
        sendToCustomer,
        sendToSupplier,
    };
}

function isNil(v) {
    return v === null || v === undefined;
}

function normalizeValue(v) {
    // 你 flattenDiff 可能返回 string/number，也可能是对象
    if (isNil(v)) return '';
    return v;
}

const RenderValue = React.memo(function RenderValue({ value, tone }) {
    const v = normalizeValue(value);

    if (isNil(v) || v === '') {
        return <span className="text-muted">—</span>;
    }

    if (typeof v === 'number') {
        return <span>{v.toLocaleString()}</span>;
    }

    // 对象/数组：用 pre + JSON 格式化，限制高度，避免撑爆
    if (typeof v === 'object') {
        const text = JSON.stringify(v, null, 2);
        return (
            <pre
                className={`mb-0 small text-break ${tone === 'new' ? 'fw-semibold' : ''}`}
                style={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    maxHeight: 180,
                    overflow: 'auto',
                }}
            >
                {text}
            </pre>
        );
    }

    // 字符串：允许换行/断词
    return (
        <span
            className={`text-break ${tone === 'new' ? 'fw-semibold' : ''}`}
            style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
            title={String(v).length > 140 ? String(v) : undefined}
        >
            {String(v)}
        </span>
    );
});

const HistoryItemRow = React.memo(function HistoryItemRow({ r, idx, isLast, open, onToggle }) {
    const collapseId = `history-${r.id}`;

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') onToggle(r.id);
    };

    return (
        <div className={`px-3 py-2 ${idx ? 'border-top' : ''}`}>
            <div className="d-flex">
                {/* Timeline */}
                <div className="me-3 d-flex flex-column align-items-center" style={{ width: 18 }}>
                    <div
                        style={{
                            width: 10,
                            height: 10,
                            borderRadius: 999,
                            background: '#6c757d',
                            marginTop: 6,
                        }}
                    />
                    {!isLast ? (
                        <div style={{ flex: 1, width: 2, background: '#e9ecef', marginTop: 6 }} />
                    ) : (
                        <div style={{ height: 8 }} />
                    )}
                </div>

                {/* Content */}
                <div className="flex-grow-1">
                    <div
                        className="d-flex justify-content-between align-items-start"
                        role="button"
                        tabIndex={0}
                        aria-expanded={open}
                        aria-controls={collapseId}
                        onClick={() => onToggle(r.id)}
                        onKeyDown={handleKeyDown}
                    >
                        <div className="pe-2" style={{ minWidth: 0 }}>
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                <Badge bg={badgeVariant(r.actionType)}>{r.actionType}</Badge>

                                {r.emailInfo ? (
                                    <>
                                        <Badge bg={r.emailInfo.customer.bg} title={r.emailInfo.customer.hint}>
                                            C: {r.emailInfo.customer.label}
                                        </Badge>
                                        <Badge bg={r.emailInfo.supplier.bg} title={r.emailInfo.supplier.hint}>
                                            S: {r.emailInfo.supplier.label}
                                        </Badge>
                                    </>
                                ) : null}

                                {r.statusChange?.from && r.statusChange?.to ? (
                                    <Badge bg="light" text="dark">
                                        {r.statusChange.from} → <span className="fw-semibold">{r.statusChange.to}</span>
                                    </Badge>
                                ) : (
                                    <Badge bg="light" text="dark">
                                        {r.changeCount} changes
                                    </Badge>
                                )}

                                <span className="fw-semibold text-truncate" style={{ maxWidth: 260 }}>
                                    {r.operator}
                                </span>
                            </div>

                            <div className="text-muted small">{formatTime(r.time)}</div>

                            {r.comment ? (
                                <div className="text-muted small mt-1">
                                    <span className="me-1">Comment:</span>
                                    <span className="fst-italic">{r.comment}</span>
                                </div>
                            ) : null}
                        </div>

                        <div className="text-muted small">{open ? 'Hide ▴' : 'Details ▾'}</div>
                    </div>

                    {open && (
                        <div id={collapseId} className="mt-2">
                            <div className="bg-light rounded p-2">
                                {r.emailInfo?.outboxId ? (
                                    <div className="text-muted small mb-2">
                                        Outbox: <span className="font-monospace">{r.emailInfo.outboxId}</span>
                                    </div>
                                ) : null}

                                <Table striped bordered hover size="sm" className="mb-0 align-middle">
                                    <thead>
                                        <tr>
                                            <th style={{ width: '28%' }}>Field</th>
                                            <th style={{ width: '36%' }}>Old</th>
                                            <th style={{ width: '36%' }}>New</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {r.rows.map((row, i) => (
                                            <tr key={i}>
                                                <td className="small">{prettyField(row.field)}</td>
                                                <td className="small text-danger text-decoration-line-through">
                                                    <RenderValue value={row.oldValue} tone="old" />
                                                </td>
                                                <td className="small text-success">
                                                    <RenderValue value={row.newValue} tone="new" />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
});

export default function RentalOrderHistoryPanel({ rentalOrderHistory }) {
    const [openId, setOpenId] = useState(null);

    const items = useMemo(() => {
        const src = Array.isArray(rentalOrderHistory) ? rentalOrderHistory : [];

        const mapped = src
            .map((h) => {
                const diff = h?.changes?.diff;
                const rows = flattenDiff(diff);
                const emailInfo = getEmailInfo(h);
                const comment = h?.changes?.comment;
                const statusChange = getStatusChange(diff);

                const hasEmailRequest = !!(emailInfo?.sendToCustomer || emailInfo?.sendToSupplier);

                return {
                    id: h.id,
                    actionType: h.actionType,
                    operator: h.operatorName || 'Unknown',
                    time: h.operatedAt,
                    comment: typeof comment === 'string' ? comment : '',
                    statusChange,
                    rows,
                    emailInfo,
                    changeCount: rows.length,
                    hasEmailRequest,
                };
            })
            .filter((x) => x.actionType === 'CREATE' || x.changeCount > 0 || x.hasEmailRequest || !!x.comment)
            .sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime());

        return mapped;
    }, [rentalOrderHistory]);

    const onToggle = useCallback((id) => {
        setOpenId((prev) => (prev === id ? null : id));
    }, []);
    console.log(items);
    return (
        <Card border="light" className="shadow-sm">
            <Card.Header className="bg-white">
                <div className="d-flex justify-content-between align-items-center">
                    <div className="fw-semibold">History</div>
                    <div className="text-muted small">{items.length} records</div>
                </div>
            </Card.Header>

            <Card.Body className="p-0">
                {items.length === 0 ? (
                    <div className="p-3 text-muted small">No history yet.</div>
                ) : (
                    <div>
                        {items.map((r, idx) => (
                            <HistoryItemRow
                                key={r.id}
                                r={r}
                                idx={idx}
                                isLast={idx === items.length - 1}
                                open={openId === r.id}
                                onToggle={onToggle}
                            />
                        ))}
                    </div>
                )}
            </Card.Body>
        </Card>
    );
}